// Global
import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Splide } from '@splidejs/react-splide';
import { isArray } from 'lodash';
import React, { createContext, useEffect, useState } from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';
import { ComponentVariants } from 'lib/context/ComponentVariants';
import { useTheme } from 'lib/context/ThemeContext';
import { CardComponents, ProductAndArticleComponent } from 'lib/templates/Feature.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';
import { Common } from 'lib/templates/Foundation.Dart.model';

// Local
import ProductSingleCard from './Component/ProductSingleCard';
import Carousel from 'components/authorable/Carousel/Carousel';
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import Button from 'helpers/Button/Button';
import Label, { ColorTypes } from 'helpers/Label/Label';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import SVG from 'helpers/SVG/SVG';
import { tailwindVariants } from './ProductCardTailwind';

export type Badge = CardComponents.ProductCard.Badge;
export type Label = Common.Label.Label;
export type ProductInfo = ProductAndArticleComponent.ProductInformation.ProductInformation;
export type ProductCard = ItemEx & CardComponents.ProductCard.ProductCardItem & Badge;
export type ProductCardProps = ComponentProps & CardComponents.ProductCard.ProductCardsList;

export const ProductCardContext = createContext('');

const ProductCard = (props: ProductCardProps): JSX.Element => {
  const { description, disclaimerText, primaryCTA, title, primaryCTAColor, primaryCTAType } =
    props?.fields || {};
  const { componentName, dataSource } = props?.rendering || {};
  const { autoPlay, isBadgeFullWidth } = props?.params || {};

  const contentRef = React.useRef<Splide>(null);
  const isBrowser = typeof window !== 'undefined';

  const [breakpoints, setBreakpoints] = useState('xs');
  const [themeBg, setThemeBg] = useState({});
  const [windowSize, setWindowSize] = useState({
    width: isBrowser ? window.innerWidth : 0,
    height: isBrowser ? window.innerHeight : 0,
  });

  const componentVariants = ComponentVariants();
  const { themeName } = useTheme();

  const context = useSitecoreContext();
  const enableRTL = context?.sitecoreContext?.enableRTL as boolean | undefined;
  const direction = enableRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    if (!isBrowser) {
      return; // Exit if it's not running in the browser
    }

    // Handle window resize
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Listen Event
    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    if (isBrowser) {
      if (windowSize.width >= 1440) {
        setBreakpoints('lg');
      } else if (windowSize.width >= 996) {
        setBreakpoints('md');
      } else if (windowSize.width >= 320) {
        setBreakpoints('sm');
      }
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isBrowser, breakpoints, windowSize.width]); // Include isBrowser in the dependency array

  useEffect(() => {
    switch (themeName) {
      case 'Off':
        setThemeBg({});
        break;
      case 'Ziploc':
        setThemeBg({
          background: 'linear-gradient(180deg, #032C87 33.13%, #0241CD 55%, #0CF 100%, #032C87)',
        });
        break;
      default:
        setThemeBg({});
        break;
    }
  }, [themeName]);

  if (!props.fields) return <></>;

  const {
    background,
    base,
    buttonClasses,
    container,
    contentCarousel,
    contentCarouselSingle,
    contentCarouselTrack,
    controlContainerClasses,
    ctaWrapper,
    descriptionText,
    iconNext,
    iconPrev,
    legalDisclaimerText,
    playPauseFill,
    productCardListingCarousel,
    svgBg,
    svgDecorLower,
    svgDecorUpper,
    titleAndDescriptionWrapperInner,
    titleAndDescriptionWrapperOuter,
    titleText,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
  } = tailwindVariants({ brand: themeName as string });

  // Unique id for component
  const id = props?.params?.RenderingIdentifier;

  return (
    <ProductCardContext.Provider value={props.rendering.uid ?? ''}>
      <div
        className={background()}
        data-component="authorable/cards/productcard"
        style={themeBg}
        id={id ? id : undefined}
        tabIndex={id ? -1 : 1}
      >
        {componentVariants?.productCardListing?.hasDecor && (
          <>
            <SVG className={svgBg()} svg={`ProductCard/Bg,Brand=${themeName}`} />
            <SVG className={svgDecorUpper()} svg={`ProductCard/Decor_Upper,Brand=${themeName}`} />
            <SVG className={svgDecorLower()} svg={`ProductCard/Decor_Lower,Brand=${themeName}`} />
          </>
        )}
        <Container className={container()}>
          <div
            className={base()}
            data-component="authorable/general/ProductCard"
            data-testid="ProductCard"
          >
            {title && (
              <div className={titleAndDescriptionWrapperOuter()}>
                <div className={titleAndDescriptionWrapperInner()}>
                  {title?.value && (
                    <Text className={titleText()} encode={false} field={title} tag="h2" />
                  )}
                  {description?.value && (
                    <div className={descriptionText()}>
                      <RichTextA11yWrapper field={description} />
                    </div>
                  )}
                </div>
                {primaryCTA?.value?.href && (
                  <div className={ctaWrapper()}>
                    <Button
                      type={
                        primaryCTAType?.value ||
                        componentVariants?.productCardListing?.primaryCta?.type
                      }
                      color={
                        primaryCTAColor?.value ||
                        componentVariants?.productCardListing?.primaryCta?.color
                      }
                      href={primaryCTA?.value?.href}
                      linktype={primaryCTA?.value?.linktype}
                      gtmEvent={{
                        event: 'cta_click',
                        type: 'primary',
                        'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                        'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                        'gtm.element.dataset.gtmDatasourceId': dataSource,
                        'gtm.element.dataset.gtmComponentName': componentName,
                      }}
                      label={primaryCTA?.value.text}
                      tag="a"
                      target={primaryCTA?.value?.target}
                    />
                  </div>
                )}
              </div>
            )}
            {isArray(props?.fields?.productCards) && props?.fields?.productCards?.length > 1 ? (
              <div className={productCardListingCarousel()}>
                <Carousel
                  key={props.rendering.dataSource}
                  autoPlay={autoPlay}
                  hasTrack={false}
                  ref={contentRef}
                  carouselClasses={contentCarousel({
                    carouselMarginRight: props?.fields?.productCards?.length > 2 ? true : false,
                  })}
                  trackClasses={contentCarouselTrack({
                    addMarginBottom: props?.fields.productCards.length as 3 | 2 | 1 | undefined,
                  })}
                  gtmEvent={{
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                  options={{
                    arrows: props?.fields?.productCards?.length > 1,
                    type: 'slide',
                    autoplay: true,
                    drag:
                      (breakpoints === 'sm' && props?.fields?.productCards?.length > 1) ||
                      props?.fields?.productCards?.length > 3
                        ? true
                        : false,
                    isNavigation: false,
                    rewind: false,
                    interval: 3600,
                    perPage: 1,
                    trimSpace: false,
                    gap: 10,
                    padding: { left: 0, right: props?.fields?.productCards?.length > 1 ? 24 : 0 },
                    pagination: false,
                    direction: direction,
                    breakpoints: {
                      576: {
                        arrows: props?.fields?.productCards?.length > 2,
                        perPage: 2,
                        gap: 10,
                        padding: { left: 0, right: 24 },
                      },
                      996: {
                        arrows: props?.fields?.productCards?.length > 3,
                        perPage: props?.fields?.productCards?.length > 2 ? 3 : 2,
                        gap: 24,
                        padding: {
                          left:
                            direction === 'rtl'
                              ? props?.fields?.productCards?.length > 2
                                ? '6.5%'
                                : ''
                              : '',
                          right:
                            direction === 'ltr'
                              ? props?.fields?.productCards?.length > 2
                                ? '6.5%'
                                : ''
                              : '0',
                        },
                      },
                    },
                  }}
                  carouselControls={{
                    controlContainerClasses: controlContainerClasses(),
                    buttonClasses: buttonClasses(),
                    buttons: {
                      prev: {
                        icon: iconPrev(),
                      },
                      next: {
                        icon: iconNext(),
                      },
                      playPause: playPauseFill(),
                    },
                    navigationButtonVariant:
                      (componentVariants?.productCardListing?.carousel?.navigation as
                        | 'white'
                        | 'brand') ?? 'brand',
                    paginationVariant:
                      (componentVariants?.productCardListing?.carousel?.pagination as ColorTypes) ??
                      'color',
                  }}
                >
                  {props?.fields?.productCards?.map((item: ProductCard, key) => {
                    return (
                      <React.Fragment key={key}>
                        <ProductSingleCard
                          {...item}
                          indexOfItem={key}
                          isBadgeFullWidth={isBadgeFullWidth}
                        />
                      </React.Fragment>
                    );
                  })}
                </Carousel>
              </div>
            ) : (
              <div className={productCardListingCarousel()}>
                <div className={contentCarouselSingle()}>
                  {props?.fields?.productCards && (
                    <React.Fragment key={0}>
                      <ProductSingleCard
                        {...props?.fields?.productCards[0]}
                        indexOfItem={0}
                        isBadgeFullWidth={isBadgeFullWidth}
                      />
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}

            {disclaimerText?.value ? (
              <LegalDisclaimer
                disclaimerText={disclaimerText}
                disclaimerClasses={legalDisclaimerText()}
              />
            ) : undefined}
          </div>
        </Container>
      </div>
    </ProductCardContext.Provider>
  );
};

export default ProductCard;
